import { z } from 'zod';
import { audienceSpecSchema } from '../audience/index';

/**
 * Contracten van de module tasks (Stap 38): checklists die afgevinkt worden op de vloer.
 *
 * "Opstart lijn 2", "schoonmaak koelcel", "veiligheidsronde": een sjabloon met items, dat volgens
 * een planning bij een ploeg verschijnt en op een telefoon afgevinkt wordt.
 *
 * Twee dingen bepalen de vorm hieronder:
 *
 * 1. **Elk item draagt wie en wanneer.** Dat is acceptatiecriterium 1 en de hele reden dat dit
 *    geen takenlijstje is: een afgevinkte veiligheidsronde zonder uitvoerder is geen bewijs, en de
 *    vraag na een incident is precies "wie heeft dit afgevinkt".
 * 2. **Een waarde buiten de grenzen is een afwijking**, geen fout. De temperatuur mag 9 graden
 *    zijn; dan is de checklist afgerond **met een afwijking**, en die gaat naar de meldingen van de
 *    vloer (Stap 36) als die module aan staat. Een checklist die weigert op te slaan, levert een
 *    operator op die de volgende keer 7 invult.
 */

/**
 * De soorten items.
 *
 * `check` is ja/nee, `number` een getal met optionele grenzen, `text` een korte opmerking en
 * `photo` een foto. Geen keuzelijsten: wie keuzes nodig heeft, maakt meer items — op een telefoon
 * naast een machine is een lijst van vier opties langzamer dan vier keer ja/nee.
 */
export const checklistItemTypeSchema = z.enum(['check', 'number', 'text', 'photo']);
export type ChecklistItemType = z.infer<typeof checklistItemTypeSchema>;

export const checklistItemSchema = z.object({
  id: z.guid(),
  label: z.string(),
  help: z.string(),
  type: checklistItemTypeSchema,
  required: z.boolean(),
  position: z.number().int(),
  /** Alleen bij `number`: de grenzen waarbuiten een waarde een afwijking is. */
  min: z.number().nullable(),
  max: z.number().nullable(),
  /** De eenheid achter het invoerveld: "°C", "bar", "stuks". */
  unit: z.string(),
});
export type ChecklistItem = z.infer<typeof checklistItemSchema>;

export const createChecklistItemRequestSchema = z.object({
  label: z.string().trim().min(1, 'Geef het item een tekst.').max(200),
  help: z.string().trim().max(500).default(''),
  type: checklistItemTypeSchema,
  required: z.boolean().default(true),
  min: z.number().nullable().default(null),
  max: z.number().nullable().default(null),
  unit: z.string().trim().max(20).default(''),
});
export type CreateChecklistItemRequest = z.infer<typeof createChecklistItemRequestSchema>;

/**
 * Hoe vaak een checklist verschijnt.
 *
 * `manual` betekent: alleen als iemand hem start. `daily` en `weekdays` zijn de twee die een
 * fabriek werkelijk gebruikt; `weekly` met een dag erbij dekt de wekelijkse ronde.
 *
 * Dit is een **eigen, kleine planner** en niet `scheduling` (Stap 14), want die module bestaat
 * niet. Waarom dat zo gelaten is en waar de naad zit, staat in ADR-0064.
 */
export const checklistScheduleSchema = z.enum(['manual', 'daily', 'weekdays', 'weekly']);
export type ChecklistSchedule = z.infer<typeof checklistScheduleSchema>;

export const checklistTemplateSchema = z.object({
  id: z.guid(),
  name: z.string(),
  description: z.string(),
  audienceDescription: z.string(),
  /**
   * Een medewerker die deze checklist hoe dan ook krijgt, naast de doelgroep (Extra 6, punt 9).
   *
   * Naast en niet in plaats van: "de opstartronde van lijn 3, en Kevin doet de weekcontrole" is
   * twee dingen tegelijk. `null` betekent: alleen de doelgroep.
   */
  assigneeId: z.guid().nullable(),
  /** De naam erbij, zodat de lijst geen tweede aanroep nodig heeft. */
  assigneeName: z.string().nullable(),
  schedule: checklistScheduleSchema,
  /** `HH:MM` in de tijdzone van het bedrijf: wanneer de checklist klaarstaat. */
  planTime: z.string(),
  /** 1 = maandag, 7 = zondag. Alleen bij `weekly`. */
  weekday: z.number().int().min(1).max(7).nullable(),
  /** Hoeveel uur na het klaarstaan hij te laat is. */
  dueHours: z.number().int(),
  active: z.boolean(),
  items: z.array(checklistItemSchema),
  openRuns: z.number().int(),
  createdAt: z.string().datetime(),
});
export type ChecklistTemplate = z.infer<typeof checklistTemplateSchema>;

export const checklistTemplateListSchema = z.object({
  items: z.array(checklistTemplateSchema),
});
export type ChecklistTemplateList = z.infer<typeof checklistTemplateListSchema>;

export const createChecklistTemplateRequestSchema = z.object({
  name: z.string().trim().min(1, 'Geef de checklist een naam.').max(120),
  description: z.string().trim().max(500).default(''),
  audience: audienceSpecSchema,
  /** Een medewerker die de checklist hoe dan ook krijgt, naast de doelgroep (Extra 6, punt 9). */
  assigneeId: z.guid().nullable().default(null),
  schedule: checklistScheduleSchema,
  planTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/, 'Gebruik een tijd als 06:00.')
    .default('06:00'),
  weekday: z.number().int().min(1).max(7).nullable().default(null),
  dueHours: z.number().int().min(1).max(168).default(8),
});
export type CreateChecklistTemplateRequest = z.infer<typeof createChecklistTemplateRequestSchema>;

export const updateChecklistTemplateRequestSchema = z.object({
  name: z.string().trim().min(1).max(120).optional(),
  description: z.string().trim().max(500).optional(),
  audience: audienceSpecSchema.optional(),
  /** `null` haalt de toewijzing weg; weglaten laat ze staan. */
  assigneeId: z.guid().nullable().optional(),
  schedule: checklistScheduleSchema.optional(),
  planTime: z
    .string()
    .regex(/^([01]\d|2[0-3]):[0-5]\d$/)
    .optional(),
  weekday: z.number().int().min(1).max(7).nullable().optional(),
  dueHours: z.number().int().min(1).max(168).optional(),
  active: z.boolean().optional(),
});
export type UpdateChecklistTemplateRequest = z.infer<typeof updateChecklistTemplateRequestSchema>;

// --- Uitvoeringen --------------------------------------------------------------------

export const runStatusSchema = z.enum(['open', 'completed', 'overdue']);
export type RunStatus = z.infer<typeof runStatusSchema>;

/**
 * Het resultaat van één item.
 *
 * `doneByName` en `doneAt` staan er **altijd** bij: dat is acceptatiecriterium 1. Een afgevinkte
 * veiligheidsronde zonder uitvoerder is geen bewijs, en bij een checklist die door twee mensen
 * van dezelfde ploeg afgewerkt wordt, is "wie deed wat" de enige vraag die telt.
 */
export const checklistResultSchema = z.object({
  itemId: z.guid(),
  /** Ja/nee als `'ja'`/`'nee'`, een getal als tekst, een opmerking, of leeg. */
  value: z.string(),
  fileIds: z.array(z.guid()),
  comment: z.string(),
  deviation: z.boolean(),
  doneById: z.guid().nullable(),
  doneByName: z.string().nullable(),
  doneAt: z.string().datetime().nullable(),
});
export type ChecklistResult = z.infer<typeof checklistResultSchema>;

export const checklistRunSchema = z.object({
  id: z.guid(),
  templateId: z.guid(),
  templateName: z.string(),
  status: runStatusSchema,
  plannedFor: z.string().datetime(),
  dueAt: z.string().datetime(),
  completedAt: z.string().datetime().nullable(),
  completedByName: z.string().nullable(),
  items: z.array(checklistItemSchema),
  results: z.array(checklistResultSchema),
  /** Hoeveel verplichte items nog open staan. */
  remaining: z.number().int(),
  deviations: z.number().int(),
  /** Mag ík deze checklist afvinken? */
  canFill: z.boolean(),
  /**
   * Voor wie de checklist is, zoals de beheerder het las bij het opslaan: "Ploeg B · Lijn 3".
   *
   * Staat boven de lijst, en zegt bij alleen lezen aan wie hij toegewezen is (VLR-2). Een tekst en
   * geen doelgroep: de app hoort de regels van een doelgroep niet te kennen.
   */
  audienceDescription: z.string(),
});
export type ChecklistRun = z.infer<typeof checklistRunSchema>;

export const checklistRunListSchema = z.object({ items: z.array(checklistRunSchema) });
export type ChecklistRunList = z.infer<typeof checklistRunListSchema>;

/** Eén item afvinken. */
export const saveChecklistResultRequestSchema = z.object({
  value: z.string().trim().max(500).default(''),
  fileIds: z.array(z.guid()).max(3).default([]),
  comment: z.string().trim().max(500).default(''),
  /**
   * Wanneer de medewerker het deed, volgens zijn toestel.
   *
   * Nodig voor het offline afvinken (acceptatiecriterium 3): wie in een koelcel afvinkt en om
   * 14:00 bij de kantine weer netwerk heeft, hoort 06:55 in het bewijs te hebben staan en niet
   * 14:00. Zelfde regel en zelfde grenzen als bij een bevestiging (ADR-0047).
   */
  clientOccurredAt: z.string().datetime().nullable().default(null),
});
export type SaveChecklistResultRequest = z.infer<typeof saveChecklistResultRequestSchema>;

export const startRunRequestSchema = z.object({
  templateId: z.guid(),
});
export type StartRunRequest = z.infer<typeof startRunRequestSchema>;

/** Hoeveel foto's er aan één item mogen hangen. */
export const CHECKLIST_MAX_PHOTOS = 3;
